/**
 * Which door was this build opened through?
 *
 * 🔴 THE OPERATOR TIER GETS ITS OWN SUBDOMAIN, AND THIS IS THE WHOLE OF THE
 * CLIENT'S PART IN IT.
 *
 * `operator.kalqy.app` and `app.kalqy.app` serve the SAME static bundle — §10.4
 * deploys the frontend to S3 + CDN, and there is no server-side rendering to
 * branch in. So the separation cannot be "a different application"; it is one
 * application that knows which hostname it was fetched from and shows the
 * corresponding front door.
 *
 * ⚠️ WHAT THIS IS AND IS NOT, STATED PLAINLY, BECAUSE THE WORD "SEPARATE" INVITES
 * THE WRONG READING:
 *
 *   It IS   — a separate entry point. An operator gets a sign-in screen written
 *             for them, with no child tiles, no parent path and no "teachers,
 *             school admins and operators all sign in here" subtitle. A school
 *             that bookmarks the app never sees an operator door exists.
 *   It is   — a security boundary. `POST /auth/login` is one endpoint and it
 *   NOT       does not care which hostname asked; the role comes from the
 *             account, exactly as A2 requires. An attacker who guesses the
 *             operator hostname learns nothing and gains nothing, because the
 *             thing that stops them is `@Roles('SUPER_ADMIN')` on the server.
 *
 * That distinction is why this file is twenty lines of string matching and not
 * an auth mechanism. 📋 Recorded rather than assumed: if the operator tier ever
 * needs a real boundary, it is a separate origin with its own cookie scope and
 * its own WAF rule at the edge — infrastructure, not a constant in a bundle.
 */

/**
 * The subdomains that mean "this is the operator's door".
 *
 * ⚠️ `platform` MATCHES THE ROUTE NAMESPACE, `admin` DOES NOT AND IS ABSENT.
 * `routes.ts` refuses `admin/*` for the platform tier in writing: *"admin is
 * what a school admin is called everywhere else in this product… two meanings of
 * the word in one URL space is how a support ticket costs a round trip."* A host
 * called `admin.` would reintroduce the ambiguity one level up, where it is
 * harder to see.
 */
const OPERATOR_HOSTS = ['operator', 'platform']

/**
 * ⚠️ A DEV ESCAPE HATCH, AND IT IS A QUERY PARAMETER RATHER THAN AN ENV FLAG.
 * `localhost:5173` has no subdomain to give, and a `VITE_` variable would mean
 * rebuilding to switch doors — so `?operator` on the dev URL opens the operator
 * entry point in the same tab the school tier is already running in. It is read
 * from `search`, which the hash router never touches, so it survives every
 * in-app navigation.
 *
 * 🔴 IT IS NOT A PRIVILEGE. It changes which sign-in form is drawn. The account
 * still decides the role and the server still decides the answer.
 */
const DEV_QUERY = 'operator'

export function isOperatorHost(
  location: { hostname: string; search: string } = window.location,
): boolean {
  const host = location.hostname.toLowerCase()
  const first = host.split('.')[0]
  if (OPERATOR_HOSTS.includes(first)) return true

  return new URLSearchParams(location.search).has(DEV_QUERY)
}
